import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Plus, ShieldCheck, ShieldX } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { FormFooter, Grid, PageHeader, Row, Section, Tabs, Toggle } from '../components/form';
import { Alert, Badge, Button, Field, Input, Select } from '../components/ui';
import { api } from '../lib/api';

interface AttackRow {
  sourceIp: string;
  kinds: Array<{ kind: string; attempts: number }>;
  attempts: number;
  firstSeenAt: string;
  lastSeenAt: string;
  lastTarget: string | null;
  blocked: boolean;
  blockedBy: 'manual' | 'auto' | null;
}

interface IpRule {
  id: string;
  name: string;
  action: 'deny' | 'allow';
  cidr: string;
  source: 'manual' | 'auto';
  expiresAt: string | null;
  note: string | null;
  isActive: boolean;
}

const KIND_LABEL: Record<string, string> = {
  sip_unknown_endpoint: 'Guessing extension numbers',
  sip_auth_failure: 'Guessing passwords',
  api_login_failure: 'Admin login',
};

const compact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1000
      ? `${(n / 1000).toFixed(1)}k`
      : String(n);

const ago = (iso: string) => {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 90) return 'just now';
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)} h ago`;
  return `${Math.round(seconds / 86_400)} d ago`;
};

export function Security() {
  const [tab, setTab] = useState('attacks');

  return (
    <div className="space-y-5">
      <PageHeader
        title="Security"
        description="Who is trying to get in, and what this PBX does about it."
      />

      <Tabs
        tabs={[
          { id: 'attacks', label: 'Attacks' },
          { id: 'static', label: 'Static Defence' },
          { id: 'auto', label: 'Auto Defence' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'attacks' ? <Attacks /> : tab === 'static' ? <StaticDefence /> : <AutoDefence />}
    </div>
  );
}

/**
 * What is attacking, right now.
 *
 * The first screen because it answers the question an administrator actually has. It is built on
 * counters read out of Asterisk's own log, which is deliberate: the incident this was written
 * after had a firewall tool confidently reporting eight addresses as banned while every one of
 * them was still flooding the box. A count that comes from the PBX's own record of what it
 * rejected cannot be contradicted by a tool that thinks it is working.
 */
function Attacks() {
  const queryClient = useQueryClient();
  const [note, setNote] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['security-attacks'],
    queryFn: () =>
      api.get<{
        rows: AttackRow[];
        summary: { sources: number; attempts: number; blocked: number; since: string | null };
      }>('/api/v1/security/attacks'),
    // An attack is a live thing. A page that needs reloading to show it is a page nobody trusts.
    refetchInterval: 15_000,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['security-attacks'] });
    void queryClient.invalidateQueries({ queryKey: ['security-rules'] });
  };

  const block = useMutation({
    mutationFn: (sourceIp: string) => api.post('/api/v1/security/block', { sourceIp }),
    onSuccess: (_r, sourceIp) => {
      setNote(`${sourceIp} is blocked. Asterisk will refuse it once config is applied.`);
      invalidate();
    },
  });

  const unblock = useMutation({
    mutationFn: (sourceIp: string) => api.post('/api/v1/security/unblock', { sourceIp }),
    onSuccess: (_r, sourceIp) => {
      setNote(`${sourceIp} is no longer blocked.`);
      invalidate();
    },
  });

  const clear = useMutation({
    mutationFn: () => api.del('/api/v1/security/attacks'),
    onSuccess: () => {
      setNote('Counters cleared. Blocks are untouched.');
      invalidate();
    },
  });

  const summary = data?.summary;
  const rows = data?.rows ?? [];

  const columns: ReadonlyArray<Column<AttackRow>> = [
    {
      key: 'sourceIp',
      header: 'Source address',
      sort: (a, b) => a.sourceIp.localeCompare(b.sourceIp),
      render: (r) => (
        <span className="flex items-center gap-2">
          <span className="font-mono text-sm">{r.sourceIp}</span>
          {r.blocked && (
            <Badge tone={r.blockedBy === 'auto' ? 'warning' : 'success'}>
              {r.blockedBy === 'auto' ? 'auto-blocked' : 'blocked'}
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'attempts',
      header: 'Attempts',
      width: '7rem',
      sort: (a, b) => a.attempts - b.attempts,
      render: (r) => <span className="tabular-nums font-medium">{compact(r.attempts)}</span>,
    },
    {
      key: 'what',
      header: 'What they tried',
      render: (r) => (
        <span className="text-sm text-muted-foreground">
          {r.kinds.map((k) => KIND_LABEL[k.kind] ?? k.kind).join(', ')}
          {r.lastTarget ? ` — last: ${r.lastTarget}` : ''}
        </span>
      ),
    },
    {
      key: 'lastSeenAt',
      header: 'Last seen',
      width: '8rem',
      sort: (a, b) => a.lastSeenAt.localeCompare(b.lastSeenAt),
      render: (r) => <span className="text-sm text-muted-foreground">{ago(r.lastSeenAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '7rem',
      render: (r) =>
        r.blocked ? (
          <Button
            variant="ghost"
            className="h-8 px-2 text-sm"
            onClick={() => unblock.mutate(r.sourceIp)}
            disabled={unblock.isPending}
          >
            Unblock
          </Button>
        ) : (
          <Button
            variant="secondary"
            className="h-8 px-2 text-sm"
            onClick={() => block.mutate(r.sourceIp)}
            disabled={block.isPending}
          >
            <Ban className="mr-1 size-3.5" />
            Block
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {note && <Alert>{note}</Alert>}
      {block.error && <Alert>{(block.error as Error).message}</Alert>}

      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Sources seen" value={compact(summary?.sources ?? 0)} />
        <Stat label="Rejected requests" value={compact(summary?.attempts ?? 0)} />
        <Stat
          label="Currently blocked"
          value={String(summary?.blocked ?? 0)}
          tone={summary && summary.sources > 0 && summary.blocked === 0 ? 'warning' : undefined}
        />
      </div>

      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.sourceIp}
        search={(r) => `${r.sourceIp} ${r.lastTarget ?? ''}`}
        searchPlaceholder="Search by address or extension tried"
        toolbar={
          <Button variant="secondary" onClick={() => clear.mutate()} disabled={rows.length === 0}>
            Clear counters
          </Button>
        }
        empty={
          isLoading ? null : (
            <div className="py-10 text-center">
              <ShieldCheck className="mx-auto size-8 text-muted-foreground" />
              <p className="mt-2 font-medium">Nothing has been rejected yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Port 5060 is open to the internet by necessity, so this list rarely stays empty for
                long on a public address.
              </p>
            </div>
          )
        }
      />

      {/*
        Said plainly, because getting this wrong is what the whole feature is a response to. An
        Asterisk ACL refuses the request; it does not stop the packet arriving, and under a real
        flood the CPU is spent before the ACL is consulted.
      */}
      <Alert>
        <strong>What Block does.</strong> It adds the address to an Asterisk ACL, so the PBX refuses
        its SIP requests before checking any password. The packets still arrive and are still parsed
        — this stops credential guessing, not a flood. Dropping traffic at the network is fail2ban's
        job on the host, and the two work together.
      </Alert>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warning' | undefined;
}) {
  return (
    <div className="rounded-[--radius] border border-border p-4">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${tone === 'warning' ? 'text-destructive' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}

/** Rules written by hand: permanent blocks, and addresses to trust inside a blocked range. */
function StaticDefence() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '',
    action: 'deny' as 'deny' | 'allow',
    cidr: '',
    note: '',
  });

  const { data: rules = [] } = useQuery({
    queryKey: ['security-rules'],
    queryFn: () => api.get<IpRule[]>('/api/v1/security/rules'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['security-rules'] });

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/v1/security/rules', {
        name: form.name,
        action: form.action,
        cidr: form.cidr,
        ...(form.note ? { note: form.note } : {}),
        isActive: true,
      }),
    onSuccess: () => {
      setAdding(false);
      setForm({ name: '', action: 'deny', cidr: '', note: '' });
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (rows: readonly IpRule[]) => {
      for (const row of rows) await api.del(`/api/v1/security/rules/${row.id}`);
    },
    onSuccess: invalidate,
  });

  const columns: ReadonlyArray<Column<IpRule>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (r) => r.name,
    },
    {
      key: 'cidr',
      header: 'Address or range',
      render: (r) => <span className="font-mono text-sm">{r.cidr}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      width: '7rem',
      render: (r) => (
        <Badge tone={r.action === 'deny' ? 'destructive' : 'success'}>
          {r.action === 'deny' ? 'Block' : 'Allow'}
        </Badge>
      ),
    },
    {
      key: 'source',
      header: 'Added by',
      width: '9rem',
      render: (r) => (
        <span className="text-sm text-muted-foreground">
          {r.source === 'auto' ? 'Auto Defence' : 'An administrator'}
          {r.expiresAt ? ` — expires ${new Date(r.expiresAt).toLocaleString()}` : ''}
        </span>
      ),
    },
    {
      key: 'note',
      header: 'Note',
      render: (r) => <span className="text-sm text-muted-foreground">{r.note ?? ''}</span>,
    },
  ];

  if (adding) {
    return (
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Section title="Add a rule">
          <Grid>
            <Row label="Name" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Scanner range"
                required
              />
            </Row>
            <Row label="Action">
              <Select
                value={form.action}
                onChange={(v) => setForm({ ...form, action: v as 'deny' | 'allow' })}
              >
                <option value="deny">Block — refuse SIP from this address</option>
                <option value="allow">Allow — trust it even inside a blocked range</option>
              </Select>
            </Row>
            <Row
              label="Address or range"
              required
              hint="A single address (51.68.34.143) or CIDR (10.0.0.0/8)."
            >
              <Input
                value={form.cidr}
                onChange={(e) => setForm({ ...form, cidr: e.target.value })}
                className="font-mono"
                placeholder="51.68.34.143"
                required
              />
            </Row>
            <Row label="Note">
              <Input
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Why this rule exists"
              />
            </Row>
          </Grid>

          {/*
            Order matters in an Asterisk ACL and the generator emits allows last, so an allow
            inside a blocked range wins. Saying so here saves somebody discovering it by locking
            out the one address they meant to keep.
          */}
          <Alert>
            Allow rules are applied after block rules, so trusting one address inside a range you
            have blocked works as you would expect.
          </Alert>
        </Section>

        {create.error ? <Alert>{(create.error as Error).message}</Alert> : null}

        <FormFooter
          onCancel={() => setAdding(false)}
          saving={create.isPending}
          saveLabel="Add rule"
          disabled={!form.name || !form.cidr}
        />
      </form>
    );
  }

  return (
    <DataTable
      rows={rules}
      columns={columns}
      rowKey={(r) => r.id}
      search={(r) => `${r.name} ${r.cidr} ${r.note ?? ''}`}
      searchPlaceholder="Search rules"
      toolbar={
        <Button onClick={() => setAdding(true)}>
          <Plus className="mr-1.5 size-4" />
          Add
        </Button>
      }
      onDelete={(rows) => remove.mutate(rows)}
      deleteLabel={(rows) =>
        rows.length === 1
          ? `Delete "${rows[0]!.name}"? ${rows[0]!.cidr} will be able to reach this PBX again.`
          : `Delete ${rows.length} rules?`
      }
      empty={
        <div className="py-10 text-center">
          <ShieldX className="mx-auto size-8 text-muted-foreground" />
          <p className="mt-2 font-medium">No rules yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Block an address from the Attacks tab, or add a range by hand here.
          </p>
        </div>
      }
    />
  );
}

/** The threshold that blocks attackers without anybody being awake. */
function AutoDefence() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<{
    enabled: boolean;
    threshold: number;
    blockMinutes: number;
  } | null>(null);

  const { data } = useQuery({
    queryKey: ['settings', 'autoDefence'],
    queryFn: () =>
      api.get<{ enabled: boolean; threshold: number; blockMinutes: number }>(
        '/api/v1/settings/autoDefence',
      ),
  });

  const value = form ?? data ?? null;

  const save = useMutation({
    mutationFn: () => api.put('/api/v1/settings/autoDefence', value),
    onSuccess: () => {
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['settings', 'autoDefence'] });
    },
  });

  if (!value) return null;

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Section
        title="Automatic blocking"
        description="Count rejected SIP requests per address and block the ones that go too far."
      >
        <div className="flex items-center gap-3">
          <Toggle
            checked={value.enabled}
            onChange={(enabled) => setForm({ ...value, enabled })}
            label="Block attackers automatically"
          />
          <span className="text-sm">Block attackers automatically</span>
        </div>

        <Grid>
          <Row
            label="Block after"
            hint="Rejected requests from one address. A desk phone with a stale password retries every few minutes; a scanner does thousands a second."
          >
            <Field label="">
              <Input
                type="number"
                min={5}
                max={100000}
                value={value.threshold}
                onChange={(e) => setForm({ ...value, threshold: Number(e.target.value) })}
                disabled={!value.enabled}
              />
            </Field>
          </Row>
          <Row label="Block for" hint="Minutes. A permanent block is a deliberate, manual act.">
            <Input
              type="number"
              min={5}
              max={525600}
              value={value.blockMinutes}
              onChange={(e) => setForm({ ...value, blockMinutes: Number(e.target.value) })}
              disabled={!value.enabled}
            />
          </Row>
        </Grid>

        <Alert>
          Leaving this on is strongly recommended. A PBX that watches an attack, counts it
          accurately and does nothing is the state this feature was written in response to.
        </Alert>
      </Section>

      {save.error ? <Alert>{(save.error as Error).message}</Alert> : null}

      <FormFooter
        onCancel={form === null ? undefined : () => setForm(null)}
        saving={save.isPending}
        disabled={form === null}
      />
    </form>
  );
}
